import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { WEEKDAY_ABBR, buildMonthGrids } from '../utils/calendarGrid';

// Rate Shopper Reports & Analytics — scoped MVP (Performance Summary, Rate
// Positioning, Competitor Analysis). Revenue Impact and Schedule Report are
// out of scope: no outcome-tracking or cron/email infra exists yet.
// KPIs/trend come from a dedicated backend (/rate-shopper-reports, gated like
// gmReports.js since this surfaces GM-level financial data). Rate Positioning
// and Competitor Analysis reuse Rate Shopper's own /rates/calendar and
// /rates/matrix endpoints directly for on-screen display — no duplicate query
// logic here. The Export button exports whichever tab is active; its PDF is
// generated server-side from the same underlying comparison (loadCompare, in
// backend/rateShopper/compare.js) rather than re-deriving the numbers.

const inr = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`;
const signed = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}₹${Number(v).toLocaleString('en-IN')}`;
const shortDate = (iso) => { if (!iso) return ''; const d = new Date(iso + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };
const isWeekend = (iso) => { const d = new Date(iso + 'T00:00:00').getDay(); return d === 0 || d === 6; };
const deltaColor = (d) => d == null ? 'var(--theme-text-muted)' : d > 0 ? 'var(--theme-success)' : d < 0 ? 'var(--theme-danger)' : 'var(--theme-text)';

const sel = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)', fontSize: 13 };
const btn = (kind) => ({
  padding: '7px 13px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: kind === 'disabled' ? 'not-allowed' : 'pointer',
  border: kind === 'primary' ? 'none' : '1px solid var(--theme-border)',
  background: kind === 'primary' ? 'var(--theme-primary)' : 'var(--theme-surface)',
  color: kind === 'primary' ? '#fff' : 'var(--theme-text)', opacity: kind === 'disabled' ? 0.5 : 1,
});
const kpiCard = { flex: '1 1 200px', minWidth: 200, padding: '12px 16px', borderRadius: 10, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)' };
const card = { background: 'var(--theme-surface)', border: '1px solid var(--theme-border)', borderRadius: 10, padding: 16 };
const table = { borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: 13 };
const th = { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: 'var(--theme-text, #1f2937)', borderBottom: '1px solid var(--theme-border)', background: 'var(--theme-surface-2, #f1f5f9)' };
const td = { padding: '8px 12px', borderBottom: '1px solid var(--theme-border)' };
const emptyBox = { padding: 24, textAlign: 'center', color: 'var(--theme-text-muted)', border: '1px dashed var(--theme-border)', borderRadius: 8 };
const banner = (kind) => ({ margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: 13, background: kind === 'danger' ? 'rgba(239,68,68,0.1)' : '#ecfeff', border: `1px solid ${kind === 'danger' ? 'var(--theme-danger)' : '#a5f3fc'}`, color: kind === 'danger' ? 'var(--theme-danger)' : '#155e75' });

function KpiCard({ label, kpi, unit }) {
  const val = unit === 'percent' ? (kpi?.current != null ? `${kpi.current}%` : '—') : inr(kpi?.current);
  return (
    <div style={kpiCard}>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{val}</div>
      <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{label}</div>
      {kpi?.yoy_pct != null ? (
        <div style={{ fontSize: 12, fontWeight: 600, color: deltaColor(kpi.yoy_pct), marginTop: 2 }}>{kpi.yoy_pct > 0 ? '▲' : kpi.yoy_pct < 0 ? '▼' : '—'} {kpi.yoy_pct > 0 ? '+' : ''}{kpi.yoy_pct}% vs Last Year</div>
      ) : <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 2 }}>No prior-year data</div>}
    </div>
  );
}

function TrendMiniChart({ title, data, unit }) {
  return (
    <div style={{ ...card, flex: '1 1 300px', minWidth: 280 }}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{title}</div>
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e2e8f0)" />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={36} tickFormatter={(v) => unit === 'percent' ? `${v}%` : `₹${Math.round(v / 100) / 10}k`} />
            <Tooltip labelFormatter={shortDate} formatter={(v) => unit === 'percent' ? `${v}%` : inr(v)} />
            <Legend />
            <Line type="monotone" dataKey="current" name="This Period" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls />
            <Line type="monotone" dataKey="prior_year" name="Last Year" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function RateShopperReports({ token, selectedProperty }) {
  const [tab, setTab] = useState('summary');
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState('');
  const [meal, setMeal] = useState('EP');
  const [days, setDays] = useState(30);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [trend, setTrend] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [matrix, setMatrix] = useState({ dates: [], own_by_date: {}, competitors: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const api = useCallback(async (base, path) => {
    const res = await fetch(`${base}${path}`, { headers: headers() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [headers]);

  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const r = await api('/api/v1/rate-shopper', '/own-rooms');
        setRooms(r.rooms || []);
        if (r.rooms?.length) setRoom((prev) => prev || String(r.rooms[0].own_room_id));
      } catch (e) { setError(e.message); }
    })();
  }, [selectedProperty, api]);

  const load = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      if (tab === 'summary') {
        const [k, t] = await Promise.all([
          api('/api/v1/rate-shopper-reports', `/kpi-summary?days=${days}`),
          api('/api/v1/rate-shopper-reports', `/performance-trend?days=${days}`),
        ]);
        setKpiSummary(k); setTrend(t);
      } else if (tab === 'positioning') {
        const c = await api('/api/v1/rate-shopper', `/rates/calendar?days=${days}&meal=${meal}${room ? `&room=${room}` : ''}`);
        setCalendar(c.days || []);
      } else if (tab === 'competitors') {
        const m = await api('/api/v1/rate-shopper', `/rates/matrix?days=${days}&meal=${meal}${room ? `&room=${room}` : ''}`);
        setMatrix(m);
      }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [api, tab, days, meal, room, selectedProperty]);

  useEffect(() => { load(); }, [load]);

  const REPORT_FILENAME = { summary: 'performance-summary', positioning: 'rate-positioning', competitors: 'competitor-analysis' };

  // Exports whichever tab is currently active — the PDF always matches what's
  // on screen, since both are sourced from the same backend comparison pass.
  const exportReport = async () => {
    setExporting(true); setError('');
    try {
      const report = tab === 'summary' ? 'summary' : tab;
      const params = new URLSearchParams({ days: String(days), report });
      if (report !== 'summary') { if (room) params.set('room', room); params.set('meal', meal); }
      const res = await fetch(`/api/v1/rate-shopper-reports/export?${params.toString()}`, { headers: headers() });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || `Export failed (${res.status})`); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const from = report === 'summary' ? (kpiSummary?.from || '') : '';
      const to = report === 'summary' ? (kpiSummary?.to || '') : '';
      a.href = url; a.download = `${REPORT_FILENAME[report]}${from ? `-${from}-to-${to}` : `-${days}d`}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.message); } finally { setExporting(false); }
  };

  // Competitor Analysis leaderboard — per competitor across the visible dates.
  // delta = own_rate - competitor_rate (same convention as RateShopper.js's
  // Competitor Matrix): delta > 0 means you were pricier that day, delta < 0
  // means you were cheaper.
  const competitorLeaderboard = useMemo(() => {
    return matrix.competitors.map((c) => {
      const cells = matrix.dates.map((dt) => c.cells[dt]).filter((cell) => cell && cell.is_comparable && cell.rate != null);
      const avgDelta = cells.length ? Math.round(cells.reduce((s, x) => s + (x.delta || 0), 0) / cells.length) : null;
      const pricierDays = cells.filter((x) => x.delta > 0).length;
      const cheaperDays = cells.filter((x) => x.delta < 0).length;
      return { hotel_name: c.hotel_name, observations: cells.length, avg_delta: avgDelta, pricier_days: pricierDays, cheaper_days: cheaperDays };
    }).sort((a, b) => (b.observations - a.observations));
  }, [matrix]);

  if (!selectedProperty?.id) return <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Select a property to view Reports & Analytics.</div>;

  const roomName = rooms.find((r) => String(r.own_room_id) === String(room))?.name || 'room';

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Reports & Analytics</h2>
          <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>Performance, rate positioning, and competitor analysis — with year-over-year comparison.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {(tab === 'positioning' || tab === 'competitors') && (
            <>
              <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} title="Room type">
                {rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}
              </select>
              <select value={meal} onChange={(e) => setMeal(e.target.value)} style={sel} title="Meal plan">
                {['EP', 'CP', 'MAP', 'AP'].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </>
          )}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel} title="Horizon">
            {[30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button onClick={exportReport} style={btn('primary')} disabled={exporting}>{exporting ? 'Exporting…' : `⭳ Export ${tab === 'summary' ? 'Performance Summary' : tab === 'positioning' ? 'Rate Positioning' : 'Competitor Analysis'}`}</button>
          <button disabled title="Coming soon — recurring emailed reports" style={btn('disabled')}>Schedule Report 🔒</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, margin: '14px 0', borderBottom: '1px solid var(--theme-border)', paddingBottom: 8, flexWrap: 'wrap' }}>
        {[['summary', 'Performance Summary'], ['positioning', 'Rate Positioning'], ['competitors', 'Competitor Analysis']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={btn(tab === k ? 'primary' : 'ghost')} aria-pressed={tab === k}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--theme-text-muted)' }}>Loading…</div>}

      {!loading && tab === 'summary' && kpiSummary && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <KpiCard label="RevPAR" kpi={kpiSummary.kpis.revpar} unit="currency" />
            <KpiCard label="ADR" kpi={kpiSummary.kpis.adr} unit="currency" />
            <KpiCard label="Occupancy" kpi={kpiSummary.kpis.occupancy_percent} unit="percent" />
            <KpiCard label="Revenue" kpi={kpiSummary.kpis.revenue} unit="currency" />
          </div>
          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginBottom: 12 }}>
            {kpiSummary.from} – {kpiSummary.to} vs {kpiSummary.prior_from} – {kpiSummary.prior_to} ({kpiSummary.days_with_data} of {days} days have night-audit data)
          </div>
          {trend && (
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <TrendMiniChart title="RevPAR" data={trend.series.revpar} unit="currency" />
              <TrendMiniChart title="ADR" data={trend.series.adr} unit="currency" />
              <TrendMiniChart title="Occupancy %" data={trend.series.occupancy_percent} unit="percent" />
            </div>
          )}
        </div>
      )}

      {!loading && tab === 'positioning' && (
        calendar.length === 0 ? <div style={emptyBox}>No snapshot data for {roomName} · {meal} in this window.</div> : (
          buildMonthGrids(calendar).map((mg) => (
            <div key={mg.key} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>{mg.label}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                {WEEKDAY_ABBR.map((w) => <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', padding: '2px 0 6px' }}>{w}</div>)}
                {mg.cells.map((d, i) => {
                  if (!d) return <div key={i} />;
                  if (d._outsideHorizon) return <div key={i} style={{ minHeight: 74, borderRadius: 8, border: '1px solid var(--theme-border, #e2e8f0)', opacity: 0.4, padding: 8 }}><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{Number(d.stay_date.slice(8, 10))}</div></div>;
                  return (
                    <div key={i} style={{ border: '1px solid var(--theme-border)', borderRadius: 8, padding: 8, minHeight: 74, background: isWeekend(d.stay_date) ? 'var(--theme-surface-2, #f8fafc)' : 'var(--theme-surface)' }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{Number(d.stay_date.slice(8, 10))}</div>
                      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{inr(d.own_rate)}</div>
                      <div style={{ fontSize: 10, color: 'var(--theme-text-muted)' }}>mkt {inr(d.market_median)}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: deltaColor(d.delta), marginTop: 2 }}>{signed(d.delta)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )
      )}

      {!loading && tab === 'competitors' && (
        <div style={card}>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 10 }}>Per-competitor comparison for {roomName} · {meal} over {matrix.dates.length} observed day(s).</div>
          {competitorLeaderboard.length === 0 ? <div style={emptyBox}>No competitor observations in this window.</div> : (
            <table style={table}>
              <thead><tr>{['Hotel', 'Days Observed', 'Avg Gap vs You', 'Days You Were Pricier', 'Days You Were Cheaper'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {competitorLeaderboard.map((c) => (
                  <tr key={c.hotel_name}>
                    <td style={{ ...td, fontWeight: 600 }}>{c.hotel_name}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{c.observations}</td>
                    <td style={{ ...td, textAlign: 'right', color: deltaColor(c.avg_delta), fontWeight: 600 }}>{signed(c.avg_delta)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{c.pricier_days}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{c.cheaper_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default RateShopperReports;
